import { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const COOLDOWN_SECONDS = 60;

export default function VerifyEmail() {
  const location = useLocation();
  const { resendVerificationEmail } = useAuth();
  const email = (location.state as { email?: string })?.email || '';
  
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [cooldown, setCooldown] = useState(0);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = useCallback(async () => {
    if (cooldown > 0) return;
    
    if (!email) {
      setErrorMessage('이메일 주소를 찾을 수 없습니다. 다시 회원가입을 진행해주세요.');
      setResendStatus('error');
      return;
    }

    setIsResending(true);
    setResendStatus('idle');

    try {
      await resendVerificationEmail(email);
      setResendStatus('success');
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      const message = err instanceof Error ? err.message : '메일 전송에 실패했습니다';
      if (message.includes('rate') || message.includes('too many')) {
        setErrorMessage('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
        setCooldown(COOLDOWN_SECONDS);
      } else if (message.includes('already verified')) {
        setErrorMessage('이미 인증된 이메일입니다. 로그인해주세요.');
      } else {
        setErrorMessage(message);
      }
      setResendStatus('error');
    } finally {
      setIsResending(false);
    }
  }, [email, cooldown, resendVerificationEmail]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full text-center">
        <div className="card">
          <div 
            className="w-16 h-16 bg-space-blue/10 dark:bg-space-blue/20 rounded-full flex items-center justify-center mx-auto mb-6"
            aria-hidden="true"
          >
            <svg className="w-8 h-8 text-space-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            이메일을 확인해주세요
          </h1>

          <p className="text-gray-700 dark:text-gray-300 mb-6">
            {email && (
              <>
                <span className="font-medium text-gray-900 dark:text-white">{email}</span>로
                <br />
              </>
            )}
            인증 메일을 발송했습니다.
            <br />
            메일함을 확인하고 인증 링크를 클릭해주세요.
          </p>

          {/* Progress steps */}
          <div className="flex items-center justify-center gap-2 mb-6" aria-label="인증 진행 상태">
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs" aria-hidden="true">
                ✓
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400">회원가입</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300 dark:bg-zinc-600" aria-hidden="true"></div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-space-blue text-white flex items-center justify-center text-xs animate-pulse" aria-hidden="true">
                2
              </div>
              <span className="text-xs text-space-blue font-medium">이메일 인증</span>
            </div>
            <div className="w-8 h-0.5 bg-gray-300 dark:bg-zinc-600" aria-hidden="true"></div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-zinc-600 text-gray-500 dark:text-gray-400 flex items-center justify-center text-xs" aria-hidden="true">
                3
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">완료</span>
            </div>
          </div>

          {resendStatus === 'success' && (
            <div 
              className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg text-green-600 dark:text-green-400 text-sm mb-4"
              role="status"
              aria-live="polite"
            >
              ✅ 인증 메일을 다시 보냈습니다. 메일함을 확인해주세요.
            </div>
          )}

          {resendStatus === 'error' && (
            <div 
              className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm mb-4"
              role="alert"
              aria-live="polite"
            >
              {errorMessage}
            </div>
          )}

          <div className="bg-gray-100 dark:bg-zinc-800 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              📧 메일이 오지 않았나요?
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
              <li>스팸 또는 정크 메일함을 확인해주세요</li>
              <li>이메일 주소(<span className="font-medium">{email || '입력한 주소'}</span>)가 올바른지 확인해주세요</li>
              <li>메일 도착까지 최대 5분 정도 걸릴 수 있습니다</li>
            </ul>
          </div>

          <div className="space-y-3">
            <button 
              onClick={handleResend}
              disabled={isResending || cooldown > 0}
              className="w-full btn-secondary disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-space-blue focus:ring-offset-2"
              aria-busy={isResending}
              aria-describedby={cooldown > 0 ? 'cooldown-message' : undefined}
            >
              {isResending ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  전송 중...
                </span>
              ) : cooldown > 0 ? (
                <span className="flex items-center justify-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {cooldown}초 후 재전송 가능
                </span>
              ) : (
                '인증 메일 다시 보내기'
              )}
            </button>
            {cooldown > 0 && (
              <p id="cooldown-message" className="text-xs text-gray-500 dark:text-gray-400 sr-only">
                {cooldown}초 후에 다시 전송할 수 있습니다
              </p>
            )}
            
            <div className="pt-2">
              <Link 
                to="/login" 
                className="text-space-blue hover:underline text-sm focus:outline-none focus:ring-2 focus:ring-space-blue rounded"
              >
                로그인 페이지로 돌아가기
              </Link>
            </div>
          </div>

          {/* What happens after verification */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-zinc-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              인증 완료 후 자동으로 로그인 페이지로 이동합니다.<br />
              새 비밀번호로 로그인해주세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
